import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatMonthYear } from "@/utils/datetime";
import Text from "@/components/ui/text";
import { useWinRateBreadcrumbs } from "@/components/layouts/WinRate/WinRateBreadcrumbsContext";
import DrilldownComparisonChart from "./DrilldownComparisonChart";
import { WinRateDrilldownChartDataType } from "./WinRateDrilldownContent";
import { reducePrecision } from "@/utils/numbers";
import { filter, find, isEmpty, keys, map } from "lodash";

export const CustomTooltip: React.FC<{
  data: WinRateDrilldownChartDataType;
}> = ({ data }) => {
  const { t } = useTranslation();
  const { dynamicTableData } = useWinRateBreadcrumbs();

  const renderTitle = () => {
    const date = formatMonthYear(data.date);
    return `${t("winRate.drilldownTitle")} ${date}`;
  };

  const dataKeys = filter(
    keys(data),
    (key) =>
      key !== "date" &&
      !isEmpty(find(dynamicTableData, (user) => user?.id === key))
  );

  const comparisonChartDatasets = useMemo(() => {
    return map(dataKeys, (key) => {
      const userData = find(dynamicTableData, (user) => user?.id === key);
      const value = data[key as keyof WinRateDrilldownChartDataType];
      return {
        name: key,
        value: reducePrecision(Number(value) || 0, 0),
        userData
      };
    });
  }, [data]);

  return (
    <div className="relative">
      <Text
        as="div"
        data-test="pipeline-funnel-tooltip"
        variant="body"
        className="z-50 max-w-44 overflow-hidden rounded-md border-0 bg-surface-tooltip-toast px-3 py-1.5 shadow-md"
      >
        <div className="h-44 w-full flex flex-col gap-6">
          <Text as="div" variant="smBodySecondary" className="h-6">
            {renderTitle()}
          </Text>
          <div className="max-h-24">
            <DrilldownComparisonChart datasets={comparisonChartDatasets} />
          </div>
        </div>
      </Text>
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[10px] border-t-surface-tooltip-toast"></div>
    </div>
  );
};
